import {
  ITwilioAssignmentRow,
  ITwilioAvailableNumber,
  ITwilioMessageRow,
  ITwilioNumberPrice,
  ITwilioPhoneNumberRow,
} from '@common/interfaces/twilio'
import Base, { Wrapped } from './base'

export interface ITwilioListParams {
  clientId?: string
  filters?: Record<string, unknown>
  orderBy?: string
  orderDirection?: string
  page?: number
  perPage?: number
}

export interface ITwilioSearchParams {
  areaCode?: number
  contains?: string
  /** Two-letter state code, e.g. 'TX'. */
  inRegion?: string
  /** City name, e.g. 'Austin'. */
  inLocality?: string
  limit?: number
}

export interface ITwilioProvisionParams {
  phoneNumber: string
  friendlyName?: string
  notes?: string
  clientId?: string
  /** Free text for the assignment made on purchase; only meaningful with `clientId`. */
  assignmentNotes?: string
}

export interface ITwilioAssignParams {
  clientId: string
  notes?: string
}

export interface ITwilioReleaseParams {
  assignmentId?: string
  releaseFromTwilio?: boolean
}

export interface ITwilioSetUnsupportedParams {
  isUnsupported: boolean
  notes?: string
}

export interface ITwilioMessagesParams {
  phoneNumberId?: string
  clientId?: string
  filters?: Record<string, unknown>
  orderBy?: string
  orderDirection?: string
  page?: number
  perPage?: number
  /** Appends the page to the loaded feed instead of replacing it. */
  loadMore?: boolean
}

interface IListResult<T> {
  data: T[]
  total: number
}

export default class Twilio extends Base {
  /** One cast at the `any` boundary instead of one per method. */
  private request<T>(promise: Promise<unknown>): Wrapped<T> {
    return promise as Wrapped<T>
  }

  searchAvailableNumbers(params: ITwilioSearchParams) {
    return this.request<IListResult<ITwilioAvailableNumber> & { price: ITwilioNumberPrice | null }>(
      this.apiClient.get('twilio/available-numbers', params),
    )
  }

  listPhoneNumbers(params: ITwilioListParams = {}) {
    return this.request<IListResult<ITwilioPhoneNumberRow>>(
      this.apiClient.get('twilio/phone-numbers', params),
    )
  }

  provisionNumber(data: ITwilioProvisionParams) {
    return this.request<ITwilioPhoneNumberRow>(this.apiClient.post('twilio/phone-numbers', data))
  }

  assignNumber(phoneNumberId: string, data: ITwilioAssignParams) {
    return this.request<ITwilioAssignmentRow>(
      this.apiClient.post(`twilio/phone-numbers/${phoneNumberId}/assignments`, data),
    )
  }

  releaseNumber(phoneNumberId: string, data: ITwilioReleaseParams = {}) {
    return this.request<ITwilioPhoneNumberRow>(
      this.apiClient.post(`twilio/phone-numbers/${phoneNumberId}/release`, data),
    )
  }

  setUnsupported(phoneNumberId: string, data: ITwilioSetUnsupportedParams) {
    return this.request<ITwilioPhoneNumberRow>(
      this.apiClient.post(`twilio/phone-numbers/${phoneNumberId}/unsupported`, data),
    )
  }

  /**
   * The call audio, streamed through Atlas. Twilio protects its own recording
   * URLs with basic auth, so the browser cannot fetch one directly.
   */
  getRecording(messageId: string): Wrapped<Blob> {
    return this.request<Blob>(
      this.apiClient.get(`twilio/messages/${messageId}/recording`, { isFile: true }),
    )
  }

  listMessages(params: ITwilioMessagesParams = {}) {
    return this.request<IListResult<ITwilioMessageRow>>(
      this.apiClient.get('twilio/messages', params),
    )
  }
}
